import { useState } from "react";

import { removeDuplicates, removeDuplicatesByProperty } from "@bridgesplit/utils";
import { LoopFilterTags, Protocol } from "@bridgesplit/abf-sdk";

import { useLoopsExpanded, useUserAvailableAssets } from "../api";
import { ExploreLoopsFilters, FilterProps } from "../types/filters";
import { resetFilterIfAll, isConditionEqual } from "../utils/filters";

export const DEPOSIT_ASSETS_FILTER_KEY = "DepositAssets";
export const TAG_FILTER_KEY = "Tag";
export const INCLUDE_NEGATIVE_APY_FILTER_KEY = "IncludeNegativeApys";
export const PROTOCOLS_FILTER_KEY = "Protocols";
export const BORROW_ASSETS_FILTER_KEY = "BorrowAssets";
export const NEW_ONLY_FILTER_KEY = "NewOnly";

export const ADDITIONAL_FILTERS_KEYS: (keyof ExploreLoopsFilters)[] = [
    PROTOCOLS_FILTER_KEY,
    BORROW_ASSETS_FILTER_KEY,
    NEW_ONLY_FILTER_KEY
];

/**
 * Filter hooks work by managing pending state for filter selections before they are applied.
 * Each hook follows this pattern:
 * 1. Initialize pending state based on current filter state (enabled filters use their condition, disabled use defaults)
 * 2. Provide setPending to update selections in the UI
 * 3. Track applyEnabled to determine if changes can be applied (has selections and differs from current)
 * 4. Provide getFilterUpdate() to generate the filter update object when applying changes. Used in mobile view when the apply button is a higher level.
 * 5. Provide reset() to restore pending state to initial values
 *
 * This pattern allows for a two-stage filter process: selection (pending) and application (actual filter state).
 */

export function useDepositAssetsFilter(filterProps: FilterProps<ExploreLoopsFilters>) {
    const { filters, defaultState } = filterProps;
    const { data: loops } = useLoopsExpanded({ showHidden: false });
    const { data: assets } = useUserAvailableAssets({ skip: false });

    const tokens = removeDuplicatesByProperty(loops?.map((l) => l.collateralToken) ?? [], "mint");

    const defaultTokens = new Set([...tokens.map((t) => t.mint)]);
    const userTokens = (assets || []).filter((x) => defaultTokens.has(x.metadata.mint));
    const initTokens = filters[DEPOSIT_ASSETS_FILTER_KEY].enabled
        ? filters[DEPOSIT_ASSETS_FILTER_KEY].condition
        : defaultTokens;

    const [pending, setPending] = useState<Set<string>>(initTokens);

    const reset = () => setPending(initTokens);

    // For the drawer's onApply
    const getFilterUpdate = () => resetFilterIfAll(pending, defaultTokens, defaultState[DEPOSIT_ASSETS_FILTER_KEY]);
    const applyEnabled = !isConditionEqual(pending, filters[DEPOSIT_ASSETS_FILTER_KEY].condition) && pending.size > 0;

    return { pending, setPending, tokens, userTokens, reset, getFilterUpdate, applyEnabled };
}

export function useLoopStrategiesFilter(filterProps: FilterProps<ExploreLoopsFilters>) {
    const { filters, defaultState } = filterProps;

    // Initialize default strategies from all available loop filter tags
    const defaultStrategies = new Set(Object.values(LoopFilterTags));

    // Determine initial state based on whether filter is enabled
    const initialState = filters[TAG_FILTER_KEY].enabled
        ? (filters[TAG_FILTER_KEY].condition as Set<LoopFilterTags>)
        : defaultStrategies;

    const [pending, setPending] = useState<Set<LoopFilterTags>>(initialState);

    // Reset to initial state
    const reset = () => setPending(initialState);

    // Check if changes can be applied (has selections and differs from current filter)
    const applyEnabled = pending.size > 0 && !isConditionEqual(pending, filters[TAG_FILTER_KEY].condition);

    // Get filter update for applying changes
    const getFilterUpdate = () => resetFilterIfAll(pending, defaultStrategies, defaultState[TAG_FILTER_KEY]);

    return {
        pending,
        setPending,
        applyEnabled,
        defaultStrategies,
        initialState,
        getFilterUpdate,
        reset
    };
}

export function useIncludeNegativeApysFilter(filterProps: FilterProps<ExploreLoopsFilters>) {
    const { filters } = filterProps;
    // Exclude Negative APYs
    const [pendingIncludeNegApy, setPendingIncludeNegApy] = useState<boolean>(
        filters[INCLUDE_NEGATIVE_APY_FILTER_KEY].condition
    );

    const reset = () => setPendingIncludeNegApy(filters[INCLUDE_NEGATIVE_APY_FILTER_KEY].condition);

    const applyEnabled = !isConditionEqual(pendingIncludeNegApy, filters[INCLUDE_NEGATIVE_APY_FILTER_KEY].condition);

    const getFilterUpdate = () => ({
        IncludeNegativeApys: {
            enabled: pendingIncludeNegApy,
            condition: pendingIncludeNegApy
        }
    });

    return { pendingIncludeNegApy, setPendingIncludeNegApy, applyEnabled, getFilterUpdate, reset };
}

export function useAdditionalFilters(filterProps: FilterProps<ExploreLoopsFilters>) {
    const { filters, defaultState } = filterProps;
    const { data: loops } = useLoopsExpanded({ showHidden: false });
    const borrowTokens = loops?.map((loop) => loop.principalToken) ?? [];
    const uniqueBorrowTokens = removeDuplicates(borrowTokens);

    // Protocols
    const defaultProtocols = new Set(Object.values(Protocol));
    const initProtocols = (
        filters[PROTOCOLS_FILTER_KEY].enabled ? filters[PROTOCOLS_FILTER_KEY].condition : defaultProtocols
    ) as Set<Protocol>;
    const [pendingProtocols, setPendingProtocols] = useState<Set<Protocol>>(initProtocols);

    // Borrow Assets
    const defaultAssets = new Set(uniqueBorrowTokens.map((t) => t.mint));
    const initAssets = filters[BORROW_ASSETS_FILTER_KEY].enabled
        ? new Set<string>(filters[BORROW_ASSETS_FILTER_KEY].condition)
        : defaultAssets;
    const [pendingAssets, setPendingAssets] = useState<Set<string>>(initAssets);

    //New loops
    const [pendingNewOnly, setPendingNewOnly] = useState<boolean>(filters[NEW_ONLY_FILTER_KEY].enabled);

    const filtersChanged =
        !isConditionEqual(pendingProtocols, filters[PROTOCOLS_FILTER_KEY].condition) ||
        !isConditionEqual(pendingAssets, filters[BORROW_ASSETS_FILTER_KEY].condition) ||
        !isConditionEqual(pendingNewOnly, filters[NEW_ONLY_FILTER_KEY].condition);

    const applyEnabled = pendingAssets.size > 0 && pendingProtocols.size > 0 && filtersChanged;

    const getFilterUpdate = () => ({
        Protocols: resetFilterIfAll(pendingProtocols, defaultProtocols, defaultState[PROTOCOLS_FILTER_KEY]),
        BorrowAssets: resetFilterIfAll(pendingAssets, defaultAssets, defaultState[BORROW_ASSETS_FILTER_KEY]),
        NewOnly: {
            enabled: pendingNewOnly,
            condition: pendingNewOnly
        }
    });

    const reset = () => {
        setPendingProtocols(
            filters[PROTOCOLS_FILTER_KEY].enabled
                ? new Set<Protocol>(filters[PROTOCOLS_FILTER_KEY].condition as Iterable<Protocol>)
                : defaultProtocols
        );
        setPendingAssets(
            filters[BORROW_ASSETS_FILTER_KEY].enabled
                ? new Set<string>(filters[BORROW_ASSETS_FILTER_KEY].condition)
                : defaultAssets
        );
        setPendingNewOnly(filters[NEW_ONLY_FILTER_KEY].condition);
    };

    return {
        pendingProtocols,
        setPendingProtocols,
        pendingAssets,
        setPendingAssets,
        pendingNewOnly,
        setPendingNewOnly,
        uniqueBorrowTokens,
        applyEnabled,
        getFilterUpdate,
        reset
    };
}
